import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router';
import type { ApiError, SessionResponse } from './api';
import { endpoints } from './api';
import { FullPageMessage } from './ui';

/** The signed-in staff member, their session and their effective permissions. */
export function useSession() {
  return useQuery<SessionResponse, ApiError>({
    queryKey: ['session'],
    queryFn: endpoints.session,
  });
}

/**
 * Hiding a control the caller may not use is a courtesy, not a security
 * boundary: the API re-decides every permission server-side (docs/auth.md §4).
 */
export function usePermissions(): (permission: string) => boolean {
  const session = useSession();
  return (permission: string) => session.data?.permissions.includes(permission) ?? false;
}

export function RequireSession({ children }: { children: ReactNode }) {
  const session = useSession();

  if (session.isPending) return <FullPageMessage title="Checking your session…" />;

  if (session.error?.isUnauthenticated) return <Navigate to="/login" replace />;

  if (session.error) {
    return (
      <FullPageMessage
        title="Could not load your session"
        detail={session.error.message}
        tone="bad"
      />
    );
  }

  return <>{children}</>;
}
